/**
 * createProjection — the projection machine.
 *
 * OWNS: the ONE mapping from a price to a row index and to a pixel y, for the
 * lattice it is built on. Nothing else in the chart may re-derive either.
 *
 * WHY IT EXISTS. Candles pass an EXACT price. Cells pass a display level key,
 * which _buildDisplayInto floors onto the display lattice (ds = floor(mcap/db))
 * — a band's LOWER EDGE, not a point inside it. Both then went through
 * latticeRowIndex, which is ceil-membership: rung k owns the half-open band
 * (rung[k+1], rung[k]]. A lower edge sitting on rung[k+1] therefore resolves to
 * row k+1 — one row BELOW the volume it represents, every time, in one
 * direction. That is the cells-sit-under-the-wick bug.
 *
 * Correcting it by nudging the price is guesswork twice over: the nudge has to
 * be in the array's own units (multiplicative on a log lattice, additive on a
 * linear one), and half a DISPLAY bucket is only half a ROW when the two
 * buckets happen to match. Both mistakes were made getting here.
 *
 * So this does not nudge. When the lattice step is an integer multiple of the
 * display step — which the harmonic coarsen guarantees, since gridBucket is
 * bucketSize * 2^n and the linear display key IS bucketSize — a display index
 * maps to a row index by integer division. No float membership test, no
 * epsilon, no representative point. Exact.
 *
 * The float path remains for lattices where that relationship does not hold
 * (a display bucket that does not divide the step). It places the band's
 * CENTRE, in the right units, which is the best a single point can do.
 *
 * MUST NOT: paint, own the lattice, or write any geometry. It reads a frozen
 * mcaps array and returns numbers.
 */

const EPS = 1e-9

function isInt(x) {
  return Math.abs(x - Math.round(x)) < 1e-6
}

/**
 * mcaps       the painted lattice (descending, may carry .log)
 * rowHeight   RH in CSS px
 * keyBucket   the display key step, linear space (dispKeyBucket when !log)
 * keyRatio    the display key ratio, log space (dispRatio when log)
 */
export function createProjection({ mcaps, rowHeight, keyBucket, keyRatio }) {
  const n = mcaps ? mcaps.length : 0
  const log = !!(mcaps && mcaps.log)
  const RH = +rowHeight || 0

  // Working space: ln(price) on a log lattice, price on a linear one. Every
  // number below lives here, which is what keeps the two branches honest —
  // a ratio and a price delta are not interchangeable and mixing them is
  // exactly the class of bug this file exists to end.
  const W = log ? Math.log : (v) => v
  const top = n > 0 ? W(mcaps[0]) : 0
  const step = n > 1 ? (W(mcaps[0]) - W(mcaps[1])) : 0   // g, always > 0

  // The display step in the SAME space.
  const d = log
    ? (keyRatio > 0 ? Math.log(keyRatio) : 0)
    : (+keyBucket || 0)

  // Integer path available? Needs the row step to be a whole number of display
  // steps AND the lattice top to sit on the display grid (same phase). Both
  // hold when the lattice is anchored on multiples of a bucket the display
  // also uses — the normal case — and neither is assumed.
  const mult = (d > 0 && step > 0) ? step / d : 0
  const dsTop = d > 0 ? top / d : 0
  const exact = mult >= 1 && isInt(mult) && isInt(dsTop)
  const multI = Math.round(mult)
  const dsTopI = Math.round(dsTop)

  const inRange = (i) => (i >= 0 && i < n) ? i : -1

  return {
    exact,
    step,
    rowStep: step,
    displayStep: d,

    /**
     * Rung index owning an EXACT price. Candles, wicks, price line, alerts.
     * UNCLAMPED, deliberately — a drop-in for bucketRowIndex. An above-grid
     * high must stay negative so its wick clips off the top of the frame
     * instead of snapping to row 0.
     */
    rowOfPrice(price) {
      if (n < 2 || !(price > 0)) return -1
      return Math.ceil((top - W(price)) / step - EPS)
    },

    /**
     * Rung index for a DISPLAY LEVEL KEY — the band's lower edge.
     * Exact path: integer division of index space, no membership test.
     * Float path: place the band's centre, in working units.
     */
    rowOfLevel(mcap) {
      if (n < 2 || !(mcap > 0)) return -1
      if (exact) {
        const ds = Math.round(W(mcap) / d)
        return inRange(Math.ceil((dsTopI - ds) / multI - EPS))
      }
      return inRange(Math.ceil((top - (W(mcap) + d / 2)) / step - EPS))
    },

    /** Row index -> its top edge in px. Cells and wick ends use this. */
    yOfRow(row) {
      return row * RH
    },

    /**
     * Snapped y — the CENTRE of the row the price lives in. This is the cells'
     * grid: a price anywhere inside a row draws at that row's middle, so
     * anything using it lands on the cell rather than floating inside it.
     */
    yOfLevel(price) {
      const r = this.rowOfPrice(price)
      return (r < 0 && n < 2) ? 0 : r * RH + RH / 2
    },

    /**
     * Continuous y for an exact price, matching mcapToY. KEY = ROW BOTTOM
     * EDGE: row i holds [mcaps[i], mcaps[i] + step) and draws from y=i*RH
     * (top, price mcaps[i-1]) to (i+1)*RH (bottom, price mcaps[i]) — the
     * same band the cells fill, so a price lands INSIDE its own cell. (Was
     * +0.5, which put the key at the row CENTRE: every candle, line and tag
     * plotted half a row above the footprint.)
     */
    yOfPrice(price) {
      if (n < 2 || !(price > 0) || !(RH > 0)) return 0
      return ((top - W(price)) / step + 1) * RH
    },

    /** Exact inverse of yOfPrice. */
    priceOfY(y) {
      if (n < 2 || !(RH > 0)) return 0
      const w = top - (y / RH - 1) * step
      return log ? Math.exp(w) : w
    },
  }
}